import { css, CSSProps } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { Link } from "@/libraries/design/components";
import { LinkProps } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations/index.ts";
import { useLocalizedHref } from "@/libraries/web-framework";

import AgileMailingListSignup from "./agile-mailing-list-signup";

// ==================================================================
// CSS styling
// ==================================================================
const containerStyle = css({
    padding: "32px 16px 16px",
    position: "relative",
    margin: "auto",
    width: "100%",
    clear: "both",
    border: "0",
    fontSize: "100%",
    verticalAlign: "baseline",
    boxSizing: "border-box",

    [media.above.sm]: {
        padding: "32px 24px 16px"
    }
});

const rowStyle = css({
    marginInlineStart: "auto",
    marginInlineEnd: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "32px",
    width: "100%",

    [media.above.sm]: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 18%) repeat(2, minmax(0,1fr))",
        gridTemplateAreas: '"logo links signup"'
    }
});

const linksWrapperStyle = css({
    gridArea: "links",
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "32px",

    [media.above.sm]: {
        gridTemplateColumns: "repeat(2, minmax(0,1fr))"
    }
});

export const ComponentStyleProps: CSSProps<unknown> = {
    margin: "0",
    padding: "0",
    border: "0",
    fontSize: "100%",
    verticalAlign: "baseline"
};

// ==================================================================
// Rendering Functions
// ==================================================================
export const AgileFooterTop = () => {
    const intl = useIntl();

    const links1: LinkProps[] = [
        {
            text: intl.formatMessage({
                id: "AgileFooter.top.agile-project-management",
                defaultMessage: "Agile project management"
            }),
            href: useLocalizedHref("/agile/project-management")
        },
        {
            text: intl.formatMessage({
                id: "AgileFooter.top.scrum",
                defaultMessage: "Scrum"
            }),
            href: useLocalizedHref("/agile/scrum")
        },
        {
            text: intl.formatMessage({
                id: "AgileFooter.top.kanban",
                defaultMessage: "Kanban"
            }),
            href: useLocalizedHref("/agile/kanban")
        },
        {
            text: intl.formatMessage({
                id: "AgileFooter.top.design",
                defaultMessage: "Design"
            }),
            href: useLocalizedHref("/agile/design")
        }
    ];

    const links2: LinkProps[] = [
        {
            text: intl.formatMessage({
                id: "AgileFooter.top.software-development",
                defaultMessage: "Software development"
            }),
            href: useLocalizedHref("/agile/software-development")
        },
        {
            text: intl.formatMessage({
                id: "AgileFooter.top.product-management",
                defaultMessage: "Product management"
            }),
            href: useLocalizedHref("/agile/product-management")
        },
        {
            text: intl.formatMessage({
                id: "AgileFooter.top.teams",
                defaultMessage: "Teams"
            }),
            href: useLocalizedHref("/agile/teams")
        },
        {
            text: intl.formatMessage({
                id: "AgileFooter.top.agile-at-scale",
                defaultMessage: "Agile at scale"
            }),
            href: useLocalizedHref("/agile/agile-at-scale")
        },
        {
            text: intl.formatMessage({
                id: "AgileFooter.top.devOps",
                defaultMessage: "DevOps"
            }),
            href: useLocalizedHref("/agile/devops")
        }
    ];

    return (
        <div css={containerStyle}>
            <div css={rowStyle}>
                <div css={css({ gridArea: "logo" })}>
                    <span
                        css={css({
                            color: wacToken("color.neutral.light1000"),
                            fontSize: "1rem",
                            letterSpacing: ".3px",
                            lineHeight: 1.25,
                            fontWeight: 500,
                            fontFamily: "Charlie Display",
                            [media.above.sm]: {
                                fontSize: "1.25rem",
                                lineHeight: 1.2
                            }
                        })}
                    >
                        <FormattedMessage
                            id="AgileFooter.top.agile-topics"
                            defaultMessage="Agile Topics"
                        />
                    </span>
                </div>
                <div css={linksWrapperStyle}>
                    <AgileLinkListContainer links={links1} />
                    <AgileLinkListContainer links={links2} />
                </div>
                <AgileMailingListSignup
                    text={intl.formatMessage({
                        id: "AgileFooter.top.sign-up-for-more-agile",
                        defaultMessage:
                            "Sign up for more agile articles and tutorials."
                    })}
                />
            </div>
        </div>
    );
};

const AgileLinkListContainer = ({ links }: { links: LinkProps[] }) => {
    return (
        <div css={css({ display: "flex", flexDirection: "column", gap: 24 })}>
            {links.map((linkProp) => (
                <div key={linkProp.text}>
                    <Link
                        css={css({
                            "color": wacToken("color.neutral.light800"),
                            "fontSize": "0.875rem",
                            "lineHeight": "1.125",
                            "textDecoration": "none",
                            "verticalAlign": "baseline",

                            "&:hover": {
                                textDecoration: "underline",
                                textShadow: "none"
                            },

                            "&:visited,&:focus": {
                                textShadow: "none"
                            }
                        })}
                        {...linkProp}
                        type="anchor"
                    />
                </div>
            ))}
        </div>
    );
};

export default AgileFooterTop;
